export interface ScreenshotDiff {
  /** True when the two screenshots are the same size and no pixel differs. */
  same: boolean
  /** Share of pixels that differ, 0 to 100. */
  percent: number
  /** How much taller (or, negative, shorter) the client's screenshot is, in pixels. */
  heightDelta: number
  /** A transparent PNG to lay over the client's screenshot, marking what changed. */
  overlay: Blob | null
}

/**
 * Compare a client's screenshot with the "as designed" one. Changed pixels are painted solid
 * in the overlay and their whole row is tinted, so a two-pixel shift is still easy to find.
 */
export async function diffScreenshots(base: Blob, client: Blob): Promise<ScreenshotDiff> {
  const [a, b] = await Promise.all([createImageBitmap(base), createImageBitmap(client)])
  const width = Math.max(a.width, b.width), height = Math.max(a.height, b.height)
  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('This browser cannot compare images.')

  const pixels = (bitmap: ImageBitmap) => {
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(bitmap, 0, 0)
    return ctx.getImageData(0, 0, width, height).data
  }
  const pa = pixels(a), pb = pixels(b)
  const marks = new ImageData(width, height), out = marks.data
  let differing = 0
  for (let y = 0; y < height; y++) {
    const row = y * width * 4
    let changed = false
    for (let i = row; i < row + width * 4; i += 4) {
      if (Math.abs(pa[i]! - pb[i]!) + Math.abs(pa[i + 1]! - pb[i + 1]!) + Math.abs(pa[i + 2]! - pb[i + 2]!) <= 60) continue
      differing++
      changed = true
      out[i] = 255; out[i + 1] = 0; out[i + 2] = 170; out[i + 3] = 255
    }
    if (!changed) continue
    for (let i = row; i < row + width * 4; i += 4) {
      if (!out[i + 3]) { out[i] = 255; out[i + 1] = 0; out[i + 2] = 170; out[i + 3] = 70 }
    }
  }
  const same = !differing && a.width === b.width && a.height === b.height
  let overlay: Blob | null = null
  if (!same) {
    ctx.clearRect(0, 0, width, height)
    ctx.putImageData(marks, 0, 0)
    overlay = await canvas.convertToBlob({ type: 'image/png' })
  }
  const result = { same, percent: differing / (width * height) * 100, heightDelta: b.height - a.height, overlay }
  a.close()
  b.close()
  return result
}
